// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// The two state questions the catalog order flow keeps asking, answered once (AUT-6630, D13).
// Before this module the sidebar label, the sidebar's disabled rule, the AOI banner and the
// server's browse-mode decision each had their own reading of "is the area in place?", and
// the sidebar, the KML panel and the imagery card each their own "is imagery requested?" —
// they disagreed on an unbuffered corridor and on a rejected request. Kept free of component
// imports so the rules are unit-testable on their own; aw-server/src/utils/order_state_util.js
// carries the same rules for the server, tested against the same fixture table.

// The area is in place when the order has a boundary on file and footage to price: a polygon's
// acreage, or a corridor's linear feet. A raw LineString upload has footage but no boundary
// until the buffer dialog runs — it is not an area yet.
export const aoiPresent = (order: Pick<IOrder, 'boundaryFile' | 'acreage' | 'orderLinearFeet'> | null | undefined): boolean =>
  !!order?.boundaryFile && ((order.acreage ?? 0) > 0 || (order.orderLinearFeet ?? 0) > 0);

// A corridor whose buffer dialog was closed (or whose page was reloaded before it ran): the
// centerline is stored, the boundary is not.
export const isUnbufferedCorridor = (order: Pick<IOrder, 'boundaryFile' | 'geomType'> | null | undefined): boolean =>
  !!order && order.geomType === 'LineString' && !order.boundaryFile;

export type ImageryRequestState = 'none' | 'pending' | 'submitted' | 'fulfilled' | 'closed';

// Only presence is read, so a request document, a lean copy or a test stub all qualify.
export interface ImageryRequestFlags {
  requested?: unknown;
  requestSubmitted?: unknown;
  uploaded?: unknown;
  notificationSent?: unknown;
  rejected?: unknown;
  // Stamped by the server when the order that carried the request is placed (AUT-6554).
  orderPlaced?: unknown;
}

// none → pending (requested, the order not yet submitted) → submitted (ops has the card) →
// fulfilled (the imagery is on the project) → closed (the order that carried the request has
// been placed: the cycle is over, nothing is locked). A rejected request is none again at any
// stage: the project is back where it was before the request.
export const imageryRequestState = (request: ImageryRequestFlags | null | undefined): ImageryRequestState => {
  if (!request || request.rejected) return 'none';
  if (request.orderPlaced) return 'closed';
  if (request.uploaded || request.notificationSent) return 'fulfilled';
  if (request.requestSubmitted) return 'submitted';
  if (request.requested) return 'pending';
  return 'none';
};

// What each state locks, named so a reader says what it means.

// Draw, Upload and Get Road Network: an open or fulfilled request pins the area it was made
// for; a closed one (its order placed) locks nothing — the project is free for the next order.
export const imageryLocksAoi = (state: ImageryRequestState): boolean => state !== 'none' && state !== 'closed';

// The sidebar's order button: once the order is submitted against the request (or the
// imagery has landed) the next step is ops's, not the customer's.
export const imageryLocksOrdering = (state: ImageryRequestState): boolean => state === 'submitted' || state === 'fulfilled';

// The order form's Submit: held for the imagery while the request is open; a fulfilled
// request has its imagery on file and submits like any other order.
export const imageryHoldsSubmit = (state: ImageryRequestState): boolean => state === 'pending' || state === 'submitted';
